import React, { useState, useRef, useEffect } from 'react';
import { Send, User, Clock, Archive, MoreVertical, Settings, Users, Check, CheckCheck } from 'lucide-react';
import { useMessaging } from '../contexts/MessagingContext';
import { useClientMessaging } from '../contexts/ClientMessagingContext';
import GroupManagementModal from './GroupManagementModal';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';

const ChatInterface = ({ currentUser, canWrite, db, isClientView = false }) => {
  // Utiliser le bon contexte selon la vue
  const messagingContext = isClientView ? useClientMessaging() : useMessaging();
  const {
    activeConversation,
    messages,
    sendMessage,
    archiveConversation
  } = messagingContext;

  const [showGroupManagement, setShowGroupManagement] = useState(false);
  const [usersCache, setUsersCache] = useState({});
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [conversationData, setConversationData] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageInputRef = useRef(null);

  // Récupérer les données des utilisateurs
  useEffect(() => {
    const fetchUserData = async (userId) => {
      if (!db || !userId) return;

      try {
        // Essayer d'abord dans employees
        let userDoc = await getDoc(doc(db, 'employees', userId));

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUsersCache(prev => {
            // Ne mettre à jour que si l'utilisateur n'est pas déjà dans le cache
            if (prev[userId]) return prev;
            return {
              ...prev,
              [userId]: {
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                photoURL: userData.photoURL || userData.profilePhoto || null,
                type: 'employee'
              }
            };
          });
          return;
        }

        // Sinon essayer dans clients
        userDoc = await getDoc(doc(db, 'clients', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUsersCache(prev => {
            // Ne mettre à jour que si l'utilisateur n'est pas déjà dans le cache
            if (prev[userId]) return prev;
            return {
              ...prev,
              [userId]: {
                firstName: userData.firstName || userData.prenom || '',
                lastName: userData.lastName || userData.nom || '',
                photoURL: userData.photoURL || userData.profilePhoto || userData.photo || null,
                type: 'client'
              }
            };
          });
        }
      } catch (error) {
        console.error('Erreur récupération utilisateur:', error);
      }
    };

    // Récupérer les données de tous les participants
    if (activeConversation) {
      activeConversation.participants.forEach(participantId => {
        if (participantId !== currentUser.userId && !usersCache[participantId]) {
          fetchUserData(participantId);
        }
      });
    }
  }, [activeConversation?.id, db, currentUser?.userId]);

  // Écouter les mises à jour de la conversation active en temps réel
  useEffect(() => {
    if (!activeConversation?.id || !db) {
      setConversationData(null);
      return;
    }

    console.log('[ChatInterface] 🎧 Écoute des mises à jour de conversation:', activeConversation.id);

    const conversationRef = doc(db, 'conversations', activeConversation.id);
    const unsubscribe = onSnapshot(conversationRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        console.log('[ChatInterface] 🔄 Conversation mise à jour:', data);
        setConversationData({
          id: snapshot.id,
          ...data
        });
      }
    }, (error) => {
      console.error('[ChatInterface] ❌ Erreur écoute conversation:', error);
    });

    return () => {
      console.log('[ChatInterface] 🔌 Déconnexion du listener conversation');
      unsubscribe();
    };
  }, [activeConversation?.id, db]);

  // Fix pour iOS - Forcer le recalcul de la hauteur au chargement et après les transitions
  useEffect(() => {
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

    if (isIOS && activeConversation) {
      // Forcer le recalcul de la hauteur du viewport
      const forceResize = () => {
        // Déclencher un resize artificiel
        window.dispatchEvent(new Event('resize'));

        // Forcer le recalcul des hauteurs CSS
        if (messagesContainerRef.current) {
          messagesContainerRef.current.style.height = '0';
          messagesContainerRef.current.offsetHeight; // Force reflow
          messagesContainerRef.current.style.height = '';
        }

        // Forcer le recalcul du container parent
        if (messagesContainerRef.current?.parentElement) {
          const parent = messagesContainerRef.current.parentElement;
          parent.style.height = '0';
          parent.offsetHeight; // Force reflow
          parent.style.height = '';
        }

        // Forcer le recalcul du body
        document.body.style.height = '0';
        document.body.offsetHeight; // Force reflow
        document.body.style.height = '';
      };

      // Exécuter immédiatement
      forceResize();

      // Et après plusieurs délais pour s'assurer que le DOM est bien chargé
      const timer1 = setTimeout(forceResize, 100);
      const timer2 = setTimeout(forceResize, 500);
      const timer3 = setTimeout(forceResize, 1000);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [activeConversation?.id]);

  // Faire défiler vers le bas quand de nouveaux messages arrivent
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fix pour iOS - Scroll au focus de l'input
  useEffect(() => {
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

    const handleFocus = (e) => {
      if (isIOS) {
        // Empêcher le comportement par défaut
        e.preventDefault();

        // Scroller la page vers le haut pour faire de la place au clavier
        setTimeout(() => {
          if (messageInputRef.current) {
            // Calculer la position de l'input
            const inputRect = messageInputRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;

            // Si l'input est trop bas, scroller pour le rendre visible
            // On laisse 50% de l'écran pour le clavier
            if (inputRect.bottom > windowHeight * 0.5) {
              window.scrollBy({
                top: inputRect.bottom - (windowHeight * 0.4),
                behavior: 'smooth'
              });
            }
          }
        }, 100);

        // Après l'ouverture du clavier, ajuster encore si nécessaire
        setTimeout(() => {
          if (messageInputRef.current) {
            messageInputRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest'
            });
          }
        }, 400);
      }
    };

    const handleResize = () => {
      if (isIOS && document.activeElement === messageInputRef.current) {
        // Réajuster quand le clavier apparaît/disparaît
        setTimeout(() => {
          if (messageInputRef.current) {
            messageInputRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
    };

    const inputElement = messageInputRef.current;
    if (inputElement && isIOS) {
      inputElement.addEventListener('focus', handleFocus);
      window.addEventListener('resize', handleResize);

      return () => {
        inputElement.removeEventListener('focus', handleFocus);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  const scrollToBottom = () => {
    if (messagesContainerRef.current && messagesEndRef.current) {
      // Utiliser scrollTop au lieu de scrollIntoView pour éviter le remontage de page
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim() || isSending || !canWrite) return;

    setIsSending(true);
    try {
      await sendMessage(newMessage);
      setNewMessage('');
      // Refocuser l'input après l'envoi pour permettre de continuer à taper
      setTimeout(() => {
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      }, 0);
    } catch (error) {
      console.error('Erreur envoi message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';

    const date = timestamp instanceof Date ? timestamp : timestamp.toDate();
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMessageDate = (timestamp) => {
    if (!timestamp) return '';

    const date = timestamp instanceof Date ? timestamp : timestamp.toDate();
    const today = new Date();

    if (date.toDateString() === today.toDateString()) {
      return "Aujourd'hui";
    }

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Hier';
    }

    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  const getOtherParticipantName = () => {
    if (!activeConversation || !currentUser) return 'Utilisateur';

    if (activeConversation.type === 'group') {
      return activeConversation.groupName || `Groupe (${activeConversation.participants.length} membres)`;
    }

    if (activeConversation.participants.length === 2) {
      const otherParticipantId = activeConversation.participants.find(
        id => id !== currentUser.userId
      );

      // Récupérer le nom depuis le cache
      if (otherParticipantId && usersCache[otherParticipantId]) {
        const user = usersCache[otherParticipantId];
        return `${user.firstName} ${user.lastName}`.trim() || 'Utilisateur sans nom';
      }

      // En attendant le chargement
      return otherParticipantId ? 'Chargement...' : 'Utilisateur inconnu';
    }

    if (activeConversation.title) {
      return activeConversation.title;
    }

    return `${activeConversation.participants.length} participants`;
  };

  const getOtherParticipantPhoto = () => {
    if (!activeConversation || !currentUser) return null;

    if (activeConversation.type !== 'group' && activeConversation.participants.length === 2) {
      const otherParticipantId = activeConversation.participants.find(
        id => id !== currentUser.userId
      );
      if (otherParticipantId && usersCache[otherParticipantId]) {
        return usersCache[otherParticipantId].photoURL;
      }
    }
    return null;
  };

  const getAvatarInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getUserPhoto = (userId) => {
    if (usersCache[userId]) {
      return usersCache[userId].photoURL;
    }
    return null;
  };

  const getUserName = (userId) => {
    if (usersCache[userId]) {
      const user = usersCache[userId];
      return `${user.firstName} ${user.lastName}`.trim();
    }
    return 'Utilisateur';
  };

  // Vérifier si un message a été lu par l'autre participant
  const isMessageRead = (message) => {
    const conversation = conversationData || activeConversation;
    if (!message || !conversation || !currentUser) return false;

    // Seulement pour les messages envoyés par l'utilisateur actuel
    if (message.senderId !== currentUser.userId) return false;

    // Pour une conversation 1-1
    if (conversation.type !== 'group' && conversation.participants.length === 2) {
      const otherParticipantId = conversation.participants.find(
        id => id !== currentUser.userId
      );

      if (otherParticipantId && conversation.lastReadTimestamp) {
        const lastReadTimestamp = conversation.lastReadTimestamp[otherParticipantId];
        if (lastReadTimestamp) {
          const readTime = lastReadTimestamp.toDate ? lastReadTimestamp.toDate() : new Date(lastReadTimestamp);
          const messageTime = message.timestamp instanceof Date ? message.timestamp : message.timestamp.toDate();
          return readTime >= messageTime;
        }
      }
    }

    // Pour les groupes, considérer lu si au moins un autre participant l'a lu
    if (conversation.type === 'group' && conversation.lastReadTimestamp) {
      const messageTime = message.timestamp instanceof Date ? message.timestamp : message.timestamp.toDate();
      return conversation.participants.some(participantId => {
        if (participantId === currentUser.userId) return false;
        const lastReadTimestamp = conversation.lastReadTimestamp[participantId];
        if (!lastReadTimestamp) return false;
        const readTime = lastReadTimestamp.toDate ? lastReadTimestamp.toDate() : new Date(lastReadTimestamp);
        return readTime >= messageTime;
      });
    }

    return false;
  };

  const getConversationSubtitle = () => {
    if (!activeConversation) return '';

    if (activeConversation.type === 'group') {
      return `${activeConversation.participants.length} membres`;
    }

    // Déterminer le libellé selon le contexte
    if (isClientView) {
      // Côté client : l'autre participant est un intervenant
      return 'Intervenant';
    } else {
      // Côté employé : déterminer selon le type de conversation
      return activeConversation.type === 'internal' ? 'Collaborateur' : 'Bénéficiaire';
    }
  };

  const handleArchiveConversation = () => {
    if (activeConversation && !activeConversation.archived) {
      archiveConversation(activeConversation.id);
    }
  };

  const handleGroupManagement = () => {
    setShowGroupManagement(true);
  };

  // Regrouper les messages par date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatMessageDate(message.timestamp);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  if (!activeConversation) {
    return null;
  }

  return (
    <div className="chat-interface">
      {/* En-tête de la conversation */}
      <div className="chat-header">
        <div className="chat-header-info">
          <div className={`chat-avatar ${activeConversation.type === 'group' ? 'group-avatar' : ''}`}>
            {activeConversation.type === 'group' ? (
              <Users size={20} />
            ) : getOtherParticipantPhoto() ? (
              <img src={getOtherParticipantPhoto()} alt={getOtherParticipantName()} className="chat-avatar-img" />
            ) : (
              getAvatarInitials(getOtherParticipantName())
            )}
          </div>
          <div>
            <h3 className="chat-title">{getOtherParticipantName()}</h3>
            <p className="chat-subtitle">
              {getConversationSubtitle()}
            </p>
          </div>
        </div>

        <div className="chat-actions">
          {activeConversation.type === 'group' && !activeConversation.archived && (
            <button
              className="btn-group-management"
              onClick={handleGroupManagement}
              title="Gérer le groupe"
            >
              <Settings size={16} />
            </button>
          )}
          {!activeConversation.archived && (
            <button
              className="btn-archive-chat"
              onClick={handleArchiveConversation}
              title="Archiver la conversation"
            >
              <Archive size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Zone des messages */}
      <div className="messages-container" ref={messagesContainerRef}>
        {Object.entries(groupedMessages).map(([date, dateMessages]) => (
          <div key={date} className="message-date-group">
            <div className="date-separator">
              <span>{date}</span>
            </div>

            {dateMessages.map((message, index) => {
              const isCurrentUser = message.senderId === currentUser.userId;
              const showAvatar = index === 0 ||
                dateMessages[index - 1]?.senderId !== message.senderId;
              const showSenderName = !isCurrentUser &&
                (activeConversation.type === 'group' || showAvatar);

              const senderPhoto = getUserPhoto(message.senderId);
              const senderName = message.senderId === currentUser.userId
                ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim()
                : getUserName(message.senderId);

              return (
                <div
                  key={message.id}
                  className={`message ${isCurrentUser ? 'message-outgoing' : 'message-incoming'}`}
                >
                  {!isCurrentUser && showAvatar && (
                    <div className="message-avatar">
                      {senderPhoto ? (
                        <img src={senderPhoto} alt={senderName} className="message-avatar-img" />
                      ) : (
                        getAvatarInitials(senderName)
                      )}
                    </div>
                  )}

                  <div className="message-content">
                    {showSenderName && (
                      <div className="message-sender">
                        {message.senderName}
                      </div>
                    )}

                    <div className="message-bubble">
                      <div className="message-text">
                        {message.content}
                      </div>
                      <div className="message-time">
                        <Clock size={12} />
                        {formatMessageTime(message.timestamp)}
                        {isCurrentUser && (
                          <span className="message-read-status">
                            {isMessageRead(message) ? (
                              <CheckCheck size={14} className="read-indicator" />
                            ) : (
                              <Check size={14} className="sent-indicator" />
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        ))}

        <div ref={messagesEndRef} />
      </div>

      {/* Zone de saisie */}
      {canWrite && (
        <form onSubmit={handleSendMessage} className="message-input-container">
          <div className="message-input-wrapper">
            <textarea
              ref={messageInputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Tapez votre message..."
              className="message-input"
              rows={1}
              disabled={isSending}
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || isSending}
              className="send-button"
            >
              <Send size={18} />
            </button>
          </div>
        </form>
      )}

      {/* Modal de gestion de groupe */}
      {showGroupManagement && (
        <GroupManagementModal
          onClose={() => setShowGroupManagement(false)}
          conversation={activeConversation}
          currentUser={currentUser}
          db={db}
        />
      )}
    </div>
  );
};

export default ChatInterface;
